import React, { useState } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowBack, Cancel } from '@mui/icons-material';
import { deleteProduct, updateResellingServices } from '../api/reselling.services.api';
import ProductItem from './components/Services/ProductItem';
import BasicModal from './components/Modal';
import { getServices } from '../store/actions/services';
import AlertDialogSlide from './components/CustomAlert';

export default function UpdateResellingService() {
  const dispatch = useDispatch();
  const { category } = useParams();

  const navigate = useNavigate();

  const location = useLocation();

  // Parse query parameters
  const queryParams = new URLSearchParams(location.search);
  const currentService = queryParams.get('currentService');

  const [editedItem, setEditedItem] = React.useState(null);
  const [showEdit, setShowEdit] = React.useState(false);
  const [amount, setAmount] = React.useState('');
  const services = useSelector((state) => state.services.resellingServices);
  const productServices = useSelector((state) => state.services.services);

  const [loading, setLoading] = React.useState(false);
  const [serviceProcessors, setServiceProcessors] = React.useState([]);
  const [currentServiceProcessor, setCurrentServiceProcessor] = React.useState('');
  const selectedCategory = services.filter(
    (service) => service.category?.trim() === category.trim() && service.service === currentService
  );
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [discount, setDiscount] = useState(0.0);

  const [productId, setProductId] = useState(null);

  const [currentAvailability, setCurrentAvailability] = useState(null);

  const goBack = () => navigate(-1);

  const showDiscount = selectedCategory[0]?.service === 'Airtime' || selectedCategory[0]?.service === 'Electricity';
  const canRemove = selectedCategory[0]?.service === 'Data';

  const handleEditServiceAmount = (service) => {
    setEditedItem(service);

    setProductId(service.product_id);

    setServiceProcessors(
      productServices.filter((item) => item.service === service.service && item.category === service.category)
    );

    setCurrentServiceProcessor(service.product_id);

    setAmount(service?.amount || '');
    setDiscount(service.discount);
    setCurrentAvailability(service.available ? 'available' : 'unavailable');
    setShowEdit(true);
  };

  const continueRemovingProduct = async (option) => {
    if (!option) {
      cancelRemoveProduct();
      return;
    }
    setDeleteLoading(true);

    const request = await deleteProduct(selectedProduct.id);
    if (request.ok) {
      toast.success('delete success');
      dispatch(getServices());
      setSelectedProduct(null);
      setDeleteLoading(false);
      return;
    }

    setDeleteLoading(false);
    setSelectedProduct(null);
    toast.error('Error while deleting');
  };

  const cancelRemoveProduct = () => {
    setSelectedProduct(null);
  };

  const handleUpdateAmount = async () => {
    const payload = {
      product_id: currentServiceProcessor,
      imageUrl: serviceProcessors[0]?.imageUrl || '',
      amount,
      discount,
      available: currentAvailability === 'available',
    };

    setLoading(true);

    const response = await updateResellingServices(editedItem.id, payload);
    if (response.ok) {
      setLoading(false);
      setShowEdit(false);
      toast.success(response.data.message);
      dispatch(getServices());
      return;
    }

    setLoading(false);
    toast.error(response.data.message);
  };

  const handleRemoveProduct = (product) => {
    setSelectedProduct(product);
  };

  return (
    <>
      <BasicModal isOpen={showEdit} toggleOpen={() => setShowEdit(!showEdit)}>
        <Stack spacing={2} style={{ height: '70vh', overflowY: 'scroll' }}>
          <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
            <Typography variant="h6">Update Product</Typography>
            <IconButton onClick={() => setShowEdit(!showEdit)}>
              <Cancel color="error" />
            </IconButton>
          </Stack>

          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel id="demo-simple-select-label">Product To Purchase</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={currentServiceProcessor}
              label="Service Processor"
              onChange={(event) => setCurrentServiceProcessor(event.target.value)}
            >
              {serviceProcessors.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.service === 'Airtime'
                    ? item.category
                    : `${item.category} ${item.details?.name || ''} - ${item.details?.amount || ''}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel id="demo-simple-select-label">Product Availability</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={currentAvailability}
              label="Product Availability"
              onChange={(event) => setCurrentAvailability(event.target.value)}
            >
              <MenuItem value={'available'}>Available</MenuItem>
              <MenuItem value={'unavailable'}>Unavailable</MenuItem>
            </Select>
          </FormControl>
          {showDiscount ? (
            <TextField
              fullWidth
              sx={{ my: '20px' }}
              id="outlined-required"
              label="Discount"
              type="number"
              onPaste={(e) => {
                e.preventDefault();
                return false;
              }}
              onCopy={(e) => {
                e.preventDefault();
                return false;
              }}
              value={discount}
              onChange={(event) => {
                if (Number(event.target.value) || event.target.value === '' || event.target.value === '0') {
                  if (Number(event.target.value) >= 100) {
                    setDiscount(100);
                    return;
                  }
                  setDiscount(event.target.value);
                }
              }}
            />
          ) : (
            <TextField
              fullWidth
              sx={{ my: '20px' }}
              id="outlined-required"
              label="Amount"
              type="number"
              onPaste={(e) => {
                e.preventDefault();
                return false;
              }}
              onCopy={(e) => {
                e.preventDefault();
                return false;
              }}
              value={amount}
              onChange={(event) => {
                if (Number(event.target.value) || event.target.value === '') {
                  setAmount(event.target.value);
                }
              }}
            />
          )}

          <Stack spacing={'10px'}>
            <Button variant={'contained'} marginLeft="10px" onClick={handleUpdateAmount} disabled={loading}>
              {loading ? <CircularProgress size="20px" /> : 'Update Service'}
            </Button>
            <Button variant={'outlined'} onClick={() => setShowEdit(false)}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      </BasicModal>
      <AlertDialogSlide
        dialogTitle={selectedProduct && `Delete ${selectedProduct.service} Item`}
        dialog={
          selectedProduct &&
          `Are you sure you want to remove ${selectedProduct.category} ${selectedProduct.details?.name || ''}`
        }
        loading={deleteLoading}
        onClose={cancelRemoveProduct}
        onOption={continueRemovingProduct}
        isOpen={selectedProduct !== null}
      />
      <Box padding={'20px'}>
        <Stack direction={'row'} alignItems={'center'}>
          <IconButton onClick={goBack}>
            <ArrowBack />
          </IconButton>
          <Typography>Go back</Typography>
        </Stack>
        <Typography variant="h4">Update {category}</Typography>
        <Grid container spacing={'10px'} mt={2}>
          {selectedCategory.map((item) => (
            <Grid key={item.id} item xs={6} sm={2}>
              <ProductItem
                canRemove={canRemove}
                onRemoveProduct={() => handleRemoveProduct(item)}
                onItemPress={() => handleEditServiceAmount(item)}
                title={`${category} ${showDiscount ? '' : item.name}`}
                amount={item.amount}
                discount={item?.discount}
                imageUrl={item.imageUrl}
                showDiscount={showDiscount}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    </>
  );
}
